import React, {useCallback, useEffect, useState} from 'react';
import {I18n} from 'react-redux-i18n';
import {withStyles, WithStyles} from '@material-ui/core';

import {useDispatch, useSelector} from 'react-redux';
import {SelectableEntityVerticalPairedList, Switch} from '../../components';

import {DataExportModel} from '../../types/DataExport';
import {getUserOrganizations} from '../../actions/userOrganizations/getUserOrganizations';

import {CustomInput} from '../../material-dashboard-pro-react/components/index';

import style from './style';

type DataExportOrgsTabProps = WithStyles<typeof style> & {
  // eslint-disable-next-line no-unused-vars
  onFieldChange: (key: keyof DataExportModel, value: any) => void;
  dataExport: DataExportModel;
  isEditMode: boolean;
};

function DataExportOrgsTab({
  classes,
  onFieldChange,
  dataExport,
  isEditMode,
}: DataExportOrgsTabProps) {
  const dispatch = useDispatch();

  const accountInfo = useSelector((store: any) => store.operatorLayoutReducer.accountInfo);

  const [allOrganizations, setAllOrganizations] = useState<Array<{id: string, name: string}>>([]);
  const [orgsExport, setOrgsExport] = useState<string[]>(dataExport?.organizationIds || []);
  const [filteredOrgs, setFilteredOrgs] =
    useState<Array<{id: string, name: string}>>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const orgsNotExported = filteredOrgs.filter(
    (org) => !orgsExport.includes(org.id),
  );
  const orgsExportFullInfo = filteredOrgs.filter(
    (org) => orgsExport.includes(org.id),
  );

  const moveToExport = useCallback((indices: number[]) => {
    const selectedOrgs = indices.map((index) => orgsNotExported[index].id);
    const newOrgsExport = [...orgsExport, ...selectedOrgs];

    setOrgsExport(newOrgsExport);
    onFieldChange('organizationIds', newOrgsExport);
  }, [orgsNotExported, orgsExport]);

  const moveToNotExport = useCallback((indices: number[]) => {
    const selectedOrgs = indices.map((index) => orgsExportFullInfo[index].id);
    const newOrgsExport = orgsExport.filter((id) => !selectedOrgs.includes(id));

    setOrgsExport(newOrgsExport);
    onFieldChange('organizationIds', newOrgsExport);
    if (dataExport.allOrgs) {
      onFieldChange('allOrgs', false);
    }
  }, [dataExport, orgsExportFullInfo, orgsExport]);

  useEffect(() => {
    const loadOrganizations = async() => {
      try {
        if (accountInfo.organizationsAllowed === false) {
          setAllOrganizations([]);
          return;
        }

        const organizations = await (getUserOrganizations())(dispatch);
        const formattedOrgs = organizations.map((org: any) => ({
          id: org.organizationId,
          name: org.organizationName,
        }));

        setAllOrganizations(formattedOrgs);
        setFilteredOrgs(formattedOrgs);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load organizations:', error);
      }
    };

    loadOrganizations();
  }, [dispatch]);

  useEffect(() => {
    if (!allOrganizations?.length) {
      setOrgsExport([]);
    } else if (dataExport.allOrgs) {
      setOrgsExport(allOrganizations.map((o) => o.id));
    } else {
      setOrgsExport(dataExport?.organizationIds || []);
    }
  }, [allOrganizations]);

  useEffect(() => {
    setOrgsExport(dataExport?.organizationIds || []);
  }, [dataExport.organizationIds]);

  useEffect(() => {
    if (dataExport.allOrgs && orgsNotExported.length) {
      moveToExport(orgsNotExported.map((_, index) => index));
    }
  }, [dataExport, dataExport.allOrgs]);

  useEffect(() => {
    let resultingOrgs = [];
    if (searchQuery && searchQuery.trim().length !== 0) {
      resultingOrgs = allOrganizations.filter((org) =>
        org.name.toLowerCase().includes(searchQuery.trim().toLowerCase()));
    } else {
      resultingOrgs = allOrganizations;
    }

    resultingOrgs = resultingOrgs.sort((a, b) => a.name.localeCompare(b.name));

    setFilteredOrgs(resultingOrgs);
  }, [searchQuery, allOrganizations]);

  return (
    <>
      <h3>{I18n.t('dataExportPage.editForm.organizations.headers.selectOrganizations')}</h3>

      <div className={classes.filters__switchGroup}>
        <Switch
          label={I18n.t('dataExportPage.editForm.organizations.labels.allOrganizations')}
          isChecked={dataExport.allOrgs}
          onChange={
            (e) => {
              const isChecked = e.target.checked;
              onFieldChange('allOrgs', isChecked);
            }
          }
          disabled={!isEditMode}
        />
        <CustomInput
          id="orgs-filter"
          labelText={I18n.t('policyPage.tldTab.searchFieldLabel')}
          inputProps={{
            value: searchQuery,
            name: 'searchField',
            onChange: (e: any) => setSearchQuery(e.target.value),
            type: 'text',
          }}
          formControlProps={{
            className: classes.organizations__searchField,
          }}
        />
      </div>
      <SelectableEntityVerticalPairedList
        handleMoveFromLeftToRightList={moveToExport}
        handleMoveFromRightToLeftList={moveToNotExport}
        isEditMode={isEditMode}
        leftList={orgsNotExported}
        rightList={orgsExportFullInfo}
        titleLeft={I18n.t('dataExportPage.editForm.organizations.labels.excluded')}
        titleRight={I18n.t('dataExportPage.editForm.organizations.labels.included')}
      />
    </>
  );
}

export default withStyles(style)(DataExportOrgsTab);
